import React, { useEffect } from 'react';
import { AutoConfirmedAlert } from '@adjust/dashboard-components';

import { Account } from '@adjust/dashboard-core/api/types';
import { connectWithStore } from '@common/connectWithStore';
import { SidebarId } from '@react/constants/sidebars';
import { Trace, TraceStatus, RootState } from '@redux/types';
import {
  getAccount,
  getTimezonesOptions,
  getAccountDetailsState,
  fetchData,
  updateData,
  closeAlert,
  TimezoneOption,
} from '@redux/ducks/accountDetails';

import { AccountDetailsForm, AccountDetailsFormValues } from './AccountDetailsForm';

import './AccountDetails.scss';

interface AccountDetailsStateProps {
  account: Account;
  timezones: TimezoneOption[];
  trace: Trace;
  isDataLoading: boolean;
  isSubmitting: boolean;
  isAlertOpen: boolean;
}

interface AccountDetailsDispatchProps {
  fetchAccountDetails: () => void;
  onSubmit: (values: AccountDetailsFormValues) => void;
  closeAlert: () => void;
}

interface AccountDetailsOwnProps {
  closeSidebar: () => void;
}

type AccountDetailsProps = AccountDetailsOwnProps & AccountDetailsStateProps & AccountDetailsDispatchProps;

const AccountDetailsBase: React.FC<AccountDetailsProps> = ({
  account,
  timezones,
  trace,
  isDataLoading,
  isSubmitting,
  isAlertOpen,
  fetchAccountDetails,
  onSubmit,
  closeSidebar,
  closeAlert,
}) => {
  useEffect(() => {
    fetchAccountDetails();
  }, [fetchAccountDetails]);

  // Close Alert when the component is unmounting
  useEffect(
    () => () => {
      if (isAlertOpen) {
        closeAlert();
      }
    },
    [isAlertOpen, closeAlert],
  );

  const { status, message } = trace;

  const onAlertConfirm = () => {
    if (trace.status === TraceStatus.Info) {
      closeSidebar();
    } else {
      closeAlert();
    }
  };

  const isLoading = isDataLoading || !account || !timezones || isSubmitting;

  return (
    <div id={SidebarId.AccountDetails} className="content-panel content-company-details flex-one flex-box-column">
      <div className="content-title-box without-background">
        <h3 className="content-heading">{t('label.account-details')}</h3>
      </div>

      <div className="content-inner flex-one flex-box-column">
        <AccountDetailsForm
          account={account}
          timezones={timezones}
          isLoading={isLoading}
          onSubmit={onSubmit}
          onCancel={closeSidebar}
        />
      </div>

      <AutoConfirmedAlert
        isOpen={isAlertOpen}
        type={status}
        message={message}
        onConfirm={onAlertConfirm}
        parentSidebarId={SidebarId.AccountDetails}
      />
    </div>
  );
};

const mapStateToProps = (state: RootState): AccountDetailsStateProps => {
  const { isLoading, isSubmitting, isAlertOpen, trace } = getAccountDetailsState(state);

  return {
    account: getAccount(state),
    timezones: getTimezonesOptions(state),
    isDataLoading: isLoading,
    isSubmitting,
    isAlertOpen,
    trace,
  };
};

const mapDispatchToProps: AccountDetailsDispatchProps = {
  fetchAccountDetails: fetchData,
  onSubmit: updateData,
  closeAlert,
};

export const AccountDetails: React.FC<AccountDetailsOwnProps> = connectWithStore(
  mapStateToProps,
  mapDispatchToProps,
)(AccountDetailsBase);
